import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import translate from '../../../../../commonComponents/translate';
import CheckIcon from '../../../../../commonComponents/icons/CheckIcon';
import LockIcon from '../../../../../commonComponents/icons/LockIcon';
import { formatVA } from '../../../../../services/utilityFunctions';

function daysLeftInMonth() {
  return moment().endOf('month').diff(moment().startOf('day'), 'days');
}

function monthProgress(daysLeft) {
  const daysInMonth = moment().daysInMonth();
  const elapsed = daysInMonth - (daysLeft ?? daysLeftInMonth());
  return Math.round((elapsed / daysInMonth) * 100);
}

function LevelCard({ t, levelData, isCurrent }) {
  const {
    level,
    levelName,
    skillLevel,
    levelCompleted,
    personalVA,
    groupVA,
    lines,
    daysLeft,
  } = levelData;

  const isStageComplete = stageObject =>
    stageObject && stageObject.limitAchieved;

  let stateClass;
  if (levelCompleted) {
    stateClass = 'pureliink-card-completed';
  } else if (isCurrent) {
    stateClass = 'pureliink-card-current';
  } else {
    stateClass = 'pureliink-card-locked';
  }

  return (
    <div className={`level-card ${stateClass}`}>
      {levelCompleted && (
        <div className="cardBookmark">
          <span className="cardBookmarkText">
            {t('report.arcova.levelCompleted')}
            <img
              src="/images/reports/party-hat.svg"
              alt="yay"
              className="partyHat"
            />
          </span>
        </div>
      )}
      <div className="cardHeader">
        {!levelCompleted && !isCurrent && (
          <div className="cardLockContainer">
            <LockIcon width={76} height={96} color="#FFF" />
          </div>
        )}
        <span className="levelLabel">
          {t('report.arcova.level')} {level}
        </span>
        <img
          src={skillLevel.icon}
          alt={skillLevel.name}
          className="level-card-image"
        />
        {levelCompleted && (
          <div className="completedLevelCheck">
            <CheckIcon />
          </div>
        )}
        <span className="levelLabelName">{skillLevel.name}</span>
        <span
          className="levelLabelName"
          style={{ fontSize: '0.8rem', opacity: 0.7 }}
        >
          {levelName}
        </span>
        {isCurrent && (
          <div className="progress mt-2">
            <div
              className="progress-bar"
              role="progressbar"
              style={{ width: `${monthProgress(daysLeft)}%` }}
            >
              {daysLeft != null ? daysLeft : daysLeftInMonth()}{' '}
              {t('report.arcova.daysLeft')}
            </div>
          </div>
        )}
      </div>
      <div className="cardTableBody">
        <table className="table" style={{ width: '100%' }}>
          <tbody>
            {/* Personal VA row */}
            <tr className="content">
              <td className="contentCell">
                <div className="completedStageCheck">
                  {isStageComplete(personalVA) && <CheckIcon color="#3FBC5F" />}
                </div>
                <span className="contentCellText">
                  {personalVA ? formatVA(personalVA.min) : '—'}
                </span>
              </td>
            </tr>
            {/* Group VA row */}
            <tr className="content">
              <td className="contentCell">
                <div className="completedStageCheck">
                  {isStageComplete(groupVA) && <CheckIcon color="#3FBC5F" />}
                </div>
                <span className="contentCellText">
                  {groupVA ? formatVA(groupVA.min) : '—'}
                </span>
              </td>
            </tr>
            {/* Lines row */}
            <tr className="content">
              <td className="contentCell">
                <div className="completedStageCheck">
                  {isStageComplete(lines) && <CheckIcon color="#3FBC5F" />}
                </div>
                <span className="contentCellText">
                  {lines && lines.count
                    ? `${lines.count} × ${formatVA(lines.VA)} VA`
                    : '-'}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

LevelCard.propTypes = {
  t: PropTypes.func.isRequired,
  isCurrent: PropTypes.bool,
  levelData: PropTypes.shape({
    level: PropTypes.number.isRequired,
    levelName: PropTypes.string,
    skillLevel: PropTypes.shape({
      icon: PropTypes.string,
      name: PropTypes.string,
    }).isRequired,
    levelCompleted: PropTypes.bool,
    daysLeft: PropTypes.number,
    personalVA: PropTypes.shape({
      min: PropTypes.number,
      currentValue: PropTypes.number,
      limitAchieved: PropTypes.bool,
    }),
    groupVA: PropTypes.shape({
      min: PropTypes.number,
      currentValue: PropTypes.number,
      limitAchieved: PropTypes.bool,
    }),
    lines: PropTypes.shape({
      count: PropTypes.number,
      VA: PropTypes.number,
      currentValue: PropTypes.number,
      limitAchieved: PropTypes.bool,
    }),
  }).isRequired,
};

LevelCard.defaultProps = {
  isCurrent: false,
};

export default translate(LevelCard);
